import React, { type CSSProperties } from 'react';
import { ArrowRight, Play, Youtube } from 'lucide-react';
import {
    videoListingsTokens,
    type VideoListingsColorToken,
    type VideoListingsTheme,
} from './VideoListings.tokens';

export type VideoListingsLinkComponent = React.ComponentType<{
    href: string;
    children?: React.ReactNode;
    className?: string;
    title?: string;
    style?: CSSProperties;
    'aria-label'?: string;
    [key: string]: any;
}>;

export type VideoListingsImage = {
    src?: string;
    url?: string;
    original?: string;
    thumbnail?: string;
    alt?: string;
    title?: string;
    originalAlt?: string;
    thumbnailAlt?: string;
    objectPosition?: string;
};

export type VideoListingsItem = {
    id: string | number;
    title: React.ReactNode;
    description?: React.ReactNode;
    eyebrow?: React.ReactNode;
    meta?: React.ReactNode;
    duration?: React.ReactNode;
    href?: string;
    internalHref?: string;
    pageHref?: string;
    youtubeUrl?: string;
    youtubeHref?: string;
    externalHref?: string;
    youtubeVideoId?: string;
    videoId?: string;
    image?: VideoListingsImage;
    thumbnail?: VideoListingsImage;
    imageUrl?: string;
    thumbnailUrl?: string;
    imageAlt?: string;
    imagePosition?: string;
    watchLabel?: React.ReactNode;
    watchAriaLabel?: string;
    pageLabel?: React.ReactNode;
    pageAriaLabel?: string;
    youtubeTarget?: React.HTMLAttributeAnchorTarget;
    youtubeRel?: string;
    featured?: boolean;
    accentColor?: string;
    accentColour?: string;
    accentSoftColor?: string;
    accentSoftColour?: string;
};

export type VideoListingsColors = Partial<Record<VideoListingsColorToken, string>>;

export type VideoListingsLayout = 'grid' | 'featured' | 'auto';

export type VideoListingsProps = {
    videos?: VideoListingsItem[];
    items?: VideoListingsItem[];
    Link?: VideoListingsLinkComponent;
    theme?: VideoListingsTheme;
    mode?: VideoListingsTheme;
    layout?: VideoListingsLayout;
    featuredVideoId?: string | number;
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    titleComponent?: 'h1' | 'h2' | 'h3';
    description?: React.ReactNode;
    viewAllHref?: string;
    viewAllLabel?: React.ReactNode;
    viewAllAriaLabel?: string;
    watchLabel?: React.ReactNode;
    pageLabel?: React.ReactNode;
    emptyLabel?: React.ReactNode;
    showDescriptions?: boolean;
    colors?: VideoListingsColors;
    colours?: VideoListingsColors;
    accentColor?: string;
    accentColour?: string;
    accentSoftColor?: string;
    accentSoftColour?: string;
    backgroundColor?: string;
    backgroundColour?: string;
    surfaceColor?: string;
    surfaceColour?: string;
    textColor?: string;
    textColour?: string;
    maxWidth?: number | string;
    minTileWidth?: number | string;
    imageHeight?: number | string;
    featuredImageHeight?: number | string;
    className?: string;
    style?: CSSProperties;
    ariaLabel?: string;
};

type VideoListingsStyle = CSSProperties &
    Record<`--video-listings-${string}`, string | number | undefined>;

type VideoListingsCardStyle = CSSProperties &
    Record<`--video-listing-card-${string}`, string | number | undefined>;

const t = videoListingsTokens;

const DefaultLink: VideoListingsLinkComponent = ({ href, children, ...props }) => (
    <a href={href} {...props}>
        {children}
    </a>
);

const videoListingsCss = `
.video-listings {
  --video-listings-bg: ${t.colors.dark.background};
  --video-listings-surface: ${t.colors.dark.surface};
  --video-listings-surface-elevated: ${t.colors.dark.surfaceElevated};
  --video-listings-border: ${t.colors.dark.border};
  --video-listings-text: ${t.colors.dark.text};
  --video-listings-text-soft: ${t.colors.dark.textSoft};
  --video-listings-text-muted: ${t.colors.dark.textMuted};
  --video-listings-accent: ${t.colors.dark.accent};
  --video-listings-accent-soft: ${t.colors.dark.accentSoft};
  --video-listings-accent-text: ${t.colors.dark.accentText};
  --video-listings-shadow: ${t.colors.dark.shadow};
  --video-listings-image-fallback: ${t.colors.dark.imageFallback};
  --video-listings-max-width: ${t.sizes.maxWidth}px;
  --video-listings-min-tile-width: ${t.sizes.minTileWidth}px;
  --video-listings-image-height: ${t.sizes.imageHeight}px;
  --video-listings-featured-image-height: ${t.sizes.featuredImageHeight}px;
  width: 100%;
  overflow: hidden;
  background: var(--video-listings-bg);
  color: var(--video-listings-text);
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
  container-type: inline-size;
}
.video-listings,
.video-listings * {
  box-sizing: border-box;
}
.video-listings-shell {
  width: 100%;
  max-width: var(--video-listings-max-width);
  margin: 0 auto;
  padding: ${t.spacing.shellY}px ${t.spacing.shellX}px;
}
.video-listings-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;
}
.video-listings-heading {
  min-width: 0;
  max-width: 860px;
}
.video-listings-eyebrow {
  margin: 0 0 10px;
  color: var(--video-listings-accent);
  font-size: ${t.typography.eyebrow.fontSize}px;
  line-height: ${t.typography.eyebrow.lineHeight};
  font-weight: ${t.typography.eyebrow.fontWeight};
  letter-spacing: ${t.typography.eyebrow.letterSpacing};
  text-transform: ${t.typography.eyebrow.textTransform};
}
.video-listings-title {
  margin: 0;
  color: var(--video-listings-text);
  font-size: ${t.typography.title.fontSize}px;
  line-height: ${t.typography.title.lineHeight};
  font-weight: ${t.typography.title.fontWeight};
  letter-spacing: ${t.typography.title.letterSpacing};
  text-transform: ${t.typography.title.textTransform};
  overflow-wrap: anywhere;
}
.video-listings-title-accent {
  color: var(--video-listings-accent);
}
.video-listings-description {
  max-width: 740px;
  margin: 12px 0 0;
  color: var(--video-listings-text-soft);
  font-size: ${t.typography.description.fontSize}px;
  line-height: ${t.typography.description.lineHeight};
  font-weight: ${t.typography.description.fontWeight};
  letter-spacing: ${t.typography.description.letterSpacing};
}
.video-listings-view-all {
  display: inline-flex;
  min-height: 48px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 10px;
  max-width: 100%;
  padding: 14px 22px;
  border: 1px solid color-mix(in srgb, var(--video-listings-accent) 72%, var(--video-listings-border));
  border-radius: ${t.radius.button}px;
  background: transparent;
  color: var(--video-listings-accent);
  font-size: ${t.typography.cta.fontSize}px;
  line-height: ${t.typography.cta.lineHeight};
  font-weight: ${t.typography.cta.fontWeight};
  letter-spacing: ${t.typography.cta.letterSpacing};
  text-transform: ${t.typography.cta.textTransform};
  text-decoration: none;
  transition: ${t.motion.action};
}
.video-listings-view-all:hover,
.video-listings-view-all:focus-visible {
  transform: translateY(-1px);
  background: var(--video-listings-accent-soft);
}
.video-listings-view-all:focus-visible,
.video-listings-media-link:focus-visible,
.video-listings-action:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--video-listings-accent) 32%, transparent);
  outline-offset: 3px;
}
.video-listings-featured {
  --video-listing-card-accent: var(--video-listings-accent);
  --video-listing-card-accent-soft: var(--video-listings-accent-soft);
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, 0.92fr);
  align-items: stretch;
  overflow: hidden;
  margin-bottom: 28px;
  border: 1px solid var(--video-listings-border);
  border-radius: ${t.radius.tile}px;
  background: var(--video-listings-surface);
  box-shadow: var(--video-listings-shadow);
}
.video-listings-featured-content {
  display: flex;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
  padding: 34px 38px;
}
.video-listings-featured-label {
  margin: 0 0 18px;
  color: var(--video-listing-card-accent);
  font-size: ${t.typography.meta.fontSize}px;
  line-height: ${t.typography.meta.lineHeight};
  font-weight: ${t.typography.meta.fontWeight};
  letter-spacing: ${t.typography.meta.letterSpacing};
  text-transform: ${t.typography.meta.textTransform};
}
.video-listings-featured-title {
  margin: 0;
  color: var(--video-listings-text);
  font-size: ${t.typography.featuredTitle.fontSize}px;
  line-height: ${t.typography.featuredTitle.lineHeight};
  font-weight: ${t.typography.featuredTitle.fontWeight};
  letter-spacing: ${t.typography.featuredTitle.letterSpacing};
  text-transform: ${t.typography.featuredTitle.textTransform};
}
.video-listings-rule {
  display: block;
  width: 58px;
  height: 4px;
  margin: 20px 0 0;
  border-radius: 999px;
  background: var(--video-listing-card-accent);
}
.video-listings-featured-description {
  margin: 22px 0 0;
  color: var(--video-listings-text-soft);
  font-size: ${t.typography.body.fontSize}px;
  line-height: ${t.typography.body.lineHeight};
  font-weight: ${t.typography.body.fontWeight};
  letter-spacing: ${t.typography.body.letterSpacing};
}
.video-listings-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 28px;
}
.video-listings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, var(--video-listings-min-tile-width)), 1fr));
  gap: ${t.spacing.gap}px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.video-listings-tile {
  --video-listing-card-accent: var(--video-listings-accent);
  --video-listing-card-accent-soft: var(--video-listings-accent-soft);
  min-width: 0;
}
.video-listings-card {
  display: flex;
  height: 100%;
  min-width: 0;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--video-listings-border);
  border-radius: ${t.radius.tile}px;
  background: var(--video-listings-surface);
  transition: ${t.motion.tile};
}
.video-listings-card:hover {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--video-listing-card-accent) 54%, var(--video-listings-border));
  box-shadow: var(--video-listings-shadow);
}
.video-listings-media-link,
.video-listings-media-static {
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;
  height: var(--video-listings-image-height);
  overflow: hidden;
  background: var(--video-listings-image-fallback);
  color: inherit;
  text-decoration: none;
}
.video-listings-featured .video-listings-media-link,
.video-listings-featured .video-listings-media-static {
  height: var(--video-listings-featured-image-height);
}
.video-listings-image,
.video-listings-fallback {
  width: 100%;
  height: 100%;
}
.video-listings-image {
  display: block;
  object-fit: cover;
  transition: ${t.motion.media};
}
.video-listings-media-link:hover .video-listings-image,
.video-listings-media-link:focus-visible .video-listings-image,
.video-listings-card:hover .video-listings-image {
  transform: scale(1.035);
  filter: brightness(0.82);
}
.video-listings-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--video-listing-card-accent);
  background:
    linear-gradient(135deg, var(--video-listing-card-accent-soft), transparent 44%),
    var(--video-listings-image-fallback);
}
.video-listings-play {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 2;
  display: inline-flex;
  width: 58px;
  height: 58px;
  align-items: center;
  justify-content: center;
  border: 1px solid color-mix(in srgb, var(--video-listing-card-accent) 34%, rgba(255, 255, 255, 0.46));
  border-radius: ${t.radius.play}px;
  background: var(--video-listing-card-accent);
  color: var(--video-listings-accent-text);
  box-shadow: 0 14px 30px color-mix(in srgb, var(--video-listing-card-accent) 34%, transparent);
  transform: translate(-50%, -50%);
  transition: transform 160ms ease, filter 160ms ease;
}
.video-listings-featured .video-listings-play {
  width: 66px;
  height: 66px;
}
.video-listings-media-link:hover .video-listings-play,
.video-listings-media-link:focus-visible .video-listings-play,
.video-listings-card:hover .video-listings-play {
  transform: translate(-50%, -50%) scale(1.05);
  filter: brightness(1.04);
}
.video-listings-duration {
  position: absolute;
  right: 12px;
  bottom: 12px;
  z-index: 3;
  max-width: calc(100% - 24px);
  padding: 6px 8px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.74);
  color: #ffffff;
  font-size: 12px;
  line-height: 1;
  font-weight: 800;
}
.video-listings-card-content {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
  padding: 20px 18px 18px;
}
.video-listings-card-meta {
  margin: 0 0 10px;
  color: var(--video-listing-card-accent);
  font-size: ${t.typography.meta.fontSize}px;
  line-height: ${t.typography.meta.lineHeight};
  font-weight: ${t.typography.meta.fontWeight};
  letter-spacing: ${t.typography.meta.letterSpacing};
  text-transform: ${t.typography.meta.textTransform};
}
.video-listings-card-title {
  margin: 0;
  color: var(--video-listings-text);
  font-size: ${t.typography.tileTitle.fontSize}px;
  line-height: ${t.typography.tileTitle.lineHeight};
  font-weight: ${t.typography.tileTitle.fontWeight};
  letter-spacing: ${t.typography.tileTitle.letterSpacing};
  text-transform: ${t.typography.tileTitle.textTransform};
}
.video-listings-card-title::after {
  content: "";
  display: block;
  width: 42px;
  height: 3px;
  margin-top: 14px;
  border-radius: 999px;
  background: var(--video-listing-card-accent);
}
.video-listings-card-description {
  margin: 14px 0 0;
  color: var(--video-listings-text-soft);
  font-size: 14px;
  line-height: 1.5;
  font-weight: 500;
}
.video-listings-card .video-listings-actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(118px, 1fr));
  gap: 10px;
  margin-top: auto;
  padding-top: 20px;
}
.video-listings-action {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  max-width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--video-listing-card-accent);
  border-radius: ${t.radius.button}px;
  color: var(--video-listing-card-accent);
  font-size: ${t.typography.cta.fontSize}px;
  line-height: ${t.typography.cta.lineHeight};
  font-weight: ${t.typography.cta.fontWeight};
  letter-spacing: ${t.typography.cta.letterSpacing};
  text-transform: ${t.typography.cta.textTransform};
  text-decoration: none;
  transition: ${t.motion.action};
}
.video-listings-action--primary {
  background: var(--video-listing-card-accent);
  color: var(--video-listings-accent-text);
}
.video-listings-action:hover,
.video-listings-action:focus-visible {
  transform: translateY(-1px);
}
.video-listings-action--secondary:hover,
.video-listings-action--secondary:focus-visible {
  background: var(--video-listing-card-accent-soft);
}
.video-listings-action-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.video-listings-card .video-listings-action {
  min-height: 38px;
  gap: 7px;
  padding: 10px;
  font-size: 12px;
}
.video-listings-card .video-listings-action-text {
  overflow-wrap: normal;
  white-space: nowrap;
}
.video-listings-empty {
  margin: 0;
  padding: 28px;
  border: 1px solid var(--video-listings-border);
  border-radius: ${t.radius.tile}px;
  background: var(--video-listings-surface);
  color: var(--video-listings-text-muted);
  font-size: ${t.typography.body.fontSize}px;
  line-height: ${t.typography.body.lineHeight};
}
.video-listings-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
@container (max-width: 940px) {
  .video-listings-shell {
    padding: ${t.spacing.tabletShellY}px ${t.spacing.tabletShellX}px;
  }
  .video-listings-title {
    font-size: ${t.typography.title.tabletFontSize}px;
  }
  .video-listings-description {
    font-size: ${t.typography.description.tabletFontSize}px;
  }
  .video-listings-featured {
    grid-template-columns: 1fr;
  }
  .video-listings-featured-content {
    padding: 28px;
  }
  .video-listings-featured-title {
    font-size: ${t.typography.featuredTitle.tabletFontSize}px;
  }
}
@container (max-width: 680px) {
  .video-listings-shell {
    padding: ${t.spacing.mobileShellY}px ${t.spacing.mobileShellX}px;
  }
  .video-listings-header {
    display: grid;
    align-items: start;
    gap: 18px;
  }
  .video-listings-view-all {
    width: 100%;
  }
  .video-listings-eyebrow {
    font-size: ${t.typography.eyebrow.mobileFontSize}px;
  }
  .video-listings-title {
    font-size: ${t.typography.title.mobileFontSize}px;
    line-height: 1.08;
  }
  .video-listings-description {
    font-size: ${t.typography.description.mobileFontSize}px;
  }
  .video-listings-grid {
    gap: ${t.spacing.mobileGap}px;
  }
  .video-listings-media-link,
  .video-listings-media-static,
  .video-listings-featured .video-listings-media-link,
  .video-listings-featured .video-listings-media-static {
    height: ${t.sizes.mobileImageHeight}px;
  }
  .video-listings-featured-content {
    padding: 22px 18px 20px;
  }
  .video-listings-featured-title {
    font-size: ${t.typography.featuredTitle.mobileFontSize}px;
  }
  .video-listings-featured-description {
    font-size: ${t.typography.body.mobileFontSize}px;
  }
  .video-listings-card-title {
    font-size: ${t.typography.tileTitle.mobileFontSize}px;
  }
  .video-listings-action,
  .video-listings-card .video-listings-action {
    width: 100%;
  }
  .video-listings-play,
  .video-listings-featured .video-listings-play {
    width: 52px;
    height: 52px;
  }
}
`;

const joinClassNames = (...classes: Array<string | false | null | undefined>) =>
    classes.filter(Boolean).join(' ');

const toCssSize = (value?: number | string) => {
    if (typeof value === 'number') {
        return `${value}px`;
    }

    return value;
};

const getText = (value: React.ReactNode, fallback: string) => {
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
    }

    return fallback;
};

const getImageSrc = (image?: VideoListingsImage) =>
    image?.src || image?.url || image?.original || image?.thumbnail;

const getImageAlt = (image: VideoListingsImage | undefined, fallback: string) =>
    image?.alt || image?.title || image?.originalAlt || image?.thumbnailAlt || fallback;

const getYouTubeIdFromUrl = (url?: string) => {
    if (!url) {
        return undefined;
    }

    const match = url.match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/,
    );

    return match?.[1];
};

const getVideoImageSrc = (video: VideoListingsItem) => {
    const youtubeId =
        video.youtubeVideoId ||
        video.videoId ||
        getYouTubeIdFromUrl(video.youtubeUrl || video.youtubeHref || video.externalHref);

    return (
        video.thumbnailUrl ||
        video.imageUrl ||
        getImageSrc(video.thumbnail) ||
        getImageSrc(video.image) ||
        (youtubeId ? `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg` : undefined)
    );
};

const getInternalHref = (video: VideoListingsItem) =>
    video.internalHref || video.pageHref || video.href;

const getWatchHref = (video: VideoListingsItem) =>
    video.youtubeUrl || video.youtubeHref || video.externalHref;

const getExternalRel = (video: VideoListingsItem) => {
    if (video.youtubeRel) {
        return video.youtubeRel;
    }

    return video.youtubeTarget === '_self' ? undefined : 'noopener noreferrer';
};

const renderMedia = ({
    video,
    Link,
    title,
}: {
    video: VideoListingsItem;
    Link: VideoListingsLinkComponent;
    title: string;
}) => {
    const imageSrc = getVideoImageSrc(video);
    const imageAlt =
        video.imageAlt || getImageAlt(video.thumbnail || video.image, `${title} video thumbnail`);
    const objectPosition =
        video.imagePosition || video.thumbnail?.objectPosition || video.image?.objectPosition;
    const watchHref = getWatchHref(video);
    const internalHref = getInternalHref(video);
    const mediaClassName =
        watchHref || internalHref ? 'video-listings-media-link' : 'video-listings-media-static';
    const mediaContent = (
        <>
            {imageSrc ? (
                <img
                    className="video-listings-image"
                    src={imageSrc}
                    alt={imageAlt}
                    loading="lazy"
                    decoding="async"
                    style={{ objectPosition }}
                />
            ) : (
                <span className="video-listings-fallback" aria-hidden="true">
                    <Youtube size={48} strokeWidth={1.7} />
                </span>
            )}
            <span className="video-listings-play" aria-hidden="true">
                <Play size={24} fill="currentColor" strokeWidth={0} />
            </span>
            {video.duration && <span className="video-listings-duration">{video.duration}</span>}
        </>
    );

    if (watchHref) {
        return (
            <a
                href={watchHref}
                className={mediaClassName}
                aria-label={video.watchAriaLabel || `Watch ${title}`}
                target={video.youtubeTarget || '_blank'}
                rel={getExternalRel(video)}
            >
                {mediaContent}
            </a>
        );
    }

    if (internalHref) {
        return (
            <Link
                href={internalHref}
                className={mediaClassName}
                aria-label={video.pageAriaLabel || `View ${title}`}
            >
                {mediaContent}
            </Link>
        );
    }

    return <div className={mediaClassName}>{mediaContent}</div>;
};

const renderActions = ({
    video,
    Link,
    title,
    watchLabel,
    pageLabel,
}: {
    video: VideoListingsItem;
    Link: VideoListingsLinkComponent;
    title: string;
    watchLabel: React.ReactNode;
    pageLabel: React.ReactNode;
}) => {
    const watchHref = getWatchHref(video);
    const internalHref = getInternalHref(video);

    if (!watchHref && !internalHref) {
        return null;
    }

    return (
        <div className="video-listings-actions">
            {watchHref && (
                <a
                    href={watchHref}
                    className="video-listings-action video-listings-action--primary"
                    aria-label={video.watchAriaLabel || `Watch ${title}`}
                    target={video.youtubeTarget || '_blank'}
                    rel={getExternalRel(video)}
                >
                    <Youtube size={17} aria-hidden="true" />
                    <span className="video-listings-action-text">
                        {video.watchLabel || watchLabel}
                    </span>
                </a>
            )}
            {internalHref && (
                <Link
                    href={internalHref}
                    className="video-listings-action video-listings-action--secondary"
                    aria-label={video.pageAriaLabel || `View ${title}`}
                >
                    <span className="video-listings-action-text">
                        {video.pageLabel || pageLabel}
                    </span>
                    <ArrowRight size={17} aria-hidden="true" />
                </Link>
            )}
        </div>
    );
};

const getFeaturedIndex = (
    videos: VideoListingsItem[],
    layout: VideoListingsLayout,
    featuredVideoId?: string | number,
) => {
    if (layout === 'grid' || videos.length === 0) {
        return -1;
    }

    if (featuredVideoId !== undefined) {
        return videos.findIndex((video) => video.id === featuredVideoId);
    }

    const markedIndex = videos.findIndex((video) => video.featured);

    if (markedIndex >= 0) {
        return markedIndex;
    }

    return layout === 'featured' ? 0 : -1;
};

const VideoListings = ({
    videos,
    items,
    Link = DefaultLink,
    theme = 'dark',
    mode,
    layout = 'auto',
    featuredVideoId,
    eyebrow = t.defaults.eyebrow,
    title = t.defaults.title,
    titleComponent = 'h2',
    description = t.defaults.description,
    viewAllHref,
    viewAllLabel = t.defaults.viewAllLabel,
    viewAllAriaLabel,
    watchLabel = t.defaults.watchLabel,
    pageLabel = t.defaults.pageLabel,
    emptyLabel = t.defaults.emptyLabel,
    showDescriptions = true,
    colors,
    colours,
    accentColor,
    accentColour,
    accentSoftColor,
    accentSoftColour,
    backgroundColor,
    backgroundColour,
    surfaceColor,
    surfaceColour,
    textColor,
    textColour,
    maxWidth,
    minTileWidth,
    imageHeight,
    featuredImageHeight,
    className,
    style,
    ariaLabel,
}: VideoListingsProps) => {
    const videoItems = videos || items || [];
    const resolvedTheme = mode || theme;
    const defaultColors = t.colors[resolvedTheme] || t.colors.dark;
    const colorValues = { ...colors, ...colours };
    const TitleTag = titleComponent;
    const rootStyle: VideoListingsStyle = {
        '--video-listings-bg':
            backgroundColor ||
            backgroundColour ||
            colorValues.background ||
            defaultColors.background,
        '--video-listings-surface':
            surfaceColor || surfaceColour || colorValues.surface || defaultColors.surface,
        '--video-listings-surface-elevated':
            colorValues.surfaceElevated || defaultColors.surfaceElevated,
        '--video-listings-border': colorValues.border || defaultColors.border,
        '--video-listings-text': textColor || textColour || colorValues.text || defaultColors.text,
        '--video-listings-text-soft': colorValues.textSoft || defaultColors.textSoft,
        '--video-listings-text-muted': colorValues.textMuted || defaultColors.textMuted,
        '--video-listings-accent':
            accentColor || accentColour || colorValues.accent || defaultColors.accent,
        '--video-listings-accent-soft':
            accentSoftColor ||
            accentSoftColour ||
            colorValues.accentSoft ||
            defaultColors.accentSoft,
        '--video-listings-accent-text': colorValues.accentText || defaultColors.accentText,
        '--video-listings-shadow': colorValues.shadow || defaultColors.shadow,
        '--video-listings-image-fallback': colorValues.imageFallback || defaultColors.imageFallback,
        '--video-listings-max-width': toCssSize(maxWidth) || `${t.sizes.maxWidth}px`,
        '--video-listings-min-tile-width': toCssSize(minTileWidth) || `${t.sizes.minTileWidth}px`,
        '--video-listings-image-height': toCssSize(imageHeight) || `${t.sizes.imageHeight}px`,
        '--video-listings-featured-image-height':
            toCssSize(featuredImageHeight) || `${t.sizes.featuredImageHeight}px`,
        ...style,
    };
    const hasHeader = Boolean(eyebrow || title || description || viewAllHref);
    const featuredIndex = getFeaturedIndex(videoItems, layout, featuredVideoId);
    const featuredVideo = featuredIndex >= 0 ? videoItems[featuredIndex] : undefined;
    const gridVideos =
        featuredIndex >= 0 ? videoItems.filter((_, index) => index !== featuredIndex) : videoItems;
    const sectionLabel = ariaLabel || getText(title, 'Video listings');

    return (
        <section
            className={joinClassNames(
                'video-listings',
                `video-listings--${resolvedTheme}`,
                className,
            )}
            style={rootStyle}
            aria-label={sectionLabel}
        >
            <style>{videoListingsCss}</style>

            <div className="video-listings-shell">
                {hasHeader && (
                    <div className="video-listings-header">
                        <div className="video-listings-heading">
                            {eyebrow && <p className="video-listings-eyebrow">{eyebrow}</p>}
                            {title && <TitleTag className="video-listings-title">{title}</TitleTag>}
                            {description && (
                                <p className="video-listings-description">{description}</p>
                            )}
                        </div>

                        {viewAllHref && (
                            <Link
                                href={viewAllHref}
                                className="video-listings-view-all"
                                aria-label={viewAllAriaLabel}
                            >
                                <span>{viewAllLabel}</span>
                                <ArrowRight size={18} aria-hidden="true" />
                            </Link>
                        )}
                    </div>
                )}

                {featuredVideo && (
                    <article
                        className="video-listings-featured"
                        style={
                            {
                                '--video-listing-card-accent':
                                    featuredVideo.accentColor ||
                                    featuredVideo.accentColour ||
                                    'var(--video-listings-accent)',
                                '--video-listing-card-accent-soft':
                                    featuredVideo.accentSoftColor ||
                                    featuredVideo.accentSoftColour ||
                                    'var(--video-listings-accent-soft)',
                            } as VideoListingsCardStyle
                        }
                    >
                        {renderMedia({
                            video: featuredVideo,
                            Link,
                            title: getText(featuredVideo.title, 'Featured video'),
                        })}

                        <div className="video-listings-featured-content">
                            <p className="video-listings-featured-label">
                                {featuredVideo.eyebrow || featuredVideo.meta || 'Featured video'}
                            </p>
                            <h3 className="video-listings-featured-title">{featuredVideo.title}</h3>
                            <span className="video-listings-rule" aria-hidden="true" />
                            {featuredVideo.description && (
                                <p className="video-listings-featured-description">
                                    {featuredVideo.description}
                                </p>
                            )}
                            {renderActions({
                                video: featuredVideo,
                                Link,
                                title: getText(featuredVideo.title, 'Featured video'),
                                watchLabel,
                                pageLabel,
                            })}
                        </div>
                    </article>
                )}

                {gridVideos.length > 0 ? (
                    <ul className="video-listings-grid" role="list">
                        {gridVideos.map((video, index) => {
                            const videoTitle = getText(video.title, `Video ${index + 1}`);
                            const cardStyle: VideoListingsCardStyle = {
                                '--video-listing-card-accent':
                                    video.accentColor ||
                                    video.accentColour ||
                                    'var(--video-listings-accent)',
                                '--video-listing-card-accent-soft':
                                    video.accentSoftColor ||
                                    video.accentSoftColour ||
                                    'var(--video-listings-accent-soft)',
                            };

                            return (
                                <li
                                    className="video-listings-tile"
                                    key={video.id}
                                    style={cardStyle}
                                >
                                    <article className="video-listings-card">
                                        {renderMedia({ video, Link, title: videoTitle })}

                                        <div className="video-listings-card-content">
                                            {(video.eyebrow || video.meta) && (
                                                <p className="video-listings-card-meta">
                                                    {video.eyebrow || video.meta}
                                                </p>
                                            )}
                                            <h3 className="video-listings-card-title">
                                                {video.title}
                                            </h3>
                                            {showDescriptions && video.description && (
                                                <p className="video-listings-card-description">
                                                    {video.description}
                                                </p>
                                            )}
                                            {renderActions({
                                                video,
                                                Link,
                                                title: videoTitle,
                                                watchLabel,
                                                pageLabel,
                                            })}
                                        </div>
                                    </article>
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    !featuredVideo && <p className="video-listings-empty">{emptyLabel}</p>
                )}
            </div>
        </section>
    );
};

export { videoListingsTokens };
export type { VideoListingsColorToken, VideoListingsTheme };
export default VideoListings;
